import { createEvent } from '~/utils/eventable';
import { toSnakeCase } from '~/utils/strings';
import type { BuilderContext, BuilderEvent, BuilderState, Channel } from './reducer';

type CampaignBuilderAnalyticsEvent = {
  eventLabel: string;
  eventType: string;
};

type LabelValues = Record<string, number | string | null | undefined>;

type ChannelAction =
  | 'editor_audience_skipped'
  | 'editor_close'
  | 'editor_discard'
  | 'editor_other_channel'
  | 'editor_review_back'
  | 'editor_save'
  | 'editor_save_draft'
  | 'editor_save_draft_prompt'
  | 'editor_scheduled'
  | 'editor_start'
  | 'editor_success';

const channelRecordLabels: Record<Channel, (context: BuilderContext) => LabelValues> = {
  email: context => ({ message_group_id: context.emailChannelId }),
  googlePost: context => ({ google_post_id: context.googlePostChannelId }),
  push: context => ({ push_message_group_id: context.pushChannelId }),
  socialPost: context => ({ social_post_id: context.socialChannelId }),
  text: context => ({ text_message_group_id: context.textChannelId }),
};

const formatLabel = (values: LabelValues): string => (
  Object.entries(values).map(([key, value]) => `${key}:${value ?? ''}`).join(';')
);

const campaignLabel = (state: BuilderState, extra: LabelValues = {}): string => formatLabel({
  campaign_id: state.context.campaignId,
  selected_channels: state.channels.map(channel => toSnakeCase(channel)).join(','),
  ...extra,
});

const channelEvent = (
  state: BuilderState,
  channel: Channel | undefined,
  action: ChannelAction,
  extra: LabelValues = {},
): CampaignBuilderAnalyticsEvent | null => {
  if (!channel) {
    return null;
  }

  return {
    eventLabel: campaignLabel(state, { ...channelRecordLabels[channel](state.context), ...extra }),
    eventType: `${toSnakeCase(channel)}_${action}`,
  };
};

export const trackCampaignBuilderEvent = (event: CampaignBuilderAnalyticsEvent | null) => {
  if (!event) {
    return;
  }

  createEvent({ eventableType: 'campaign_builder', eventLabel: event.eventLabel, eventType: event.eventType });
};

export const trackChannelEvent = (
  state: BuilderState,
  channel: Channel | undefined,
  action: ChannelAction,
  extra?: LabelValues,
) => {
  trackCampaignBuilderEvent(channelEvent(state, channel, action, extra));
};

export const helpMeTopicEvent = (state: BuilderState, topicType: string | null): CampaignBuilderAnalyticsEvent => ({
  eventLabel: campaignLabel(state, { selected_topic: topicType }),
  eventType: 'help_me_create_choose_topic',
});

export const builderAnalyticsEvent = (
  prevState: BuilderState,
  event: BuilderEvent,
  nextState: BuilderState,
): CampaignBuilderAnalyticsEvent | null => {
  switch (event.type) {
    case 'campaignBuilder.openChannelSelection':
      return { eventLabel: formatLabel({ campaign_id: nextState.context.campaignId }), eventType: 'start_campaign' };
    case 'campaignBuilder.openModeSelection':
      return { eventLabel: campaignLabel(nextState), eventType: 'select_channel' };
    case 'campaignBuilder.goToBuilder': {
      const channelCount = prevState.channels.length > 1 ? 'multi_channel' : 'single_channel';
      const mode = event.creationMode === 'helpMe' ? 'help_me_create' : 'create_own';
      return { eventLabel: campaignLabel(nextState), eventType: `${channelCount}_${mode}` };
    }
    case 'campaignBuilder.cancelHelpMeGeneration':
      if (prevState.step !== 'helpMeGenerating') {
        return null;
      }
      return { eventLabel: campaignLabel(prevState), eventType: 'help_me_create_generation_cancelled' };
    case 'campaignBuilder.openChannel':
      if (prevState.step !== 'completed') {
        return null;
      }
      return channelEvent(prevState, prevState.context.activeChannel, 'editor_other_channel');
    case 'campaignBuilder.openSuccessModal':
      return channelEvent(prevState, prevState.context.activeChannel, 'editor_success');
    case 'campaignBuilder.openSaveAsDraftConfirmation':
      return channelEvent(prevState, prevState.context.activeChannel, 'editor_save_draft_prompt');
    case 'campaignBuilder.exit':
      if (prevState.step == null) {
        return null;
      }
      return { eventLabel: campaignLabel(prevState, { step: prevState.step }), eventType: 'exit_campaign' };
    default:
      return null;
  }
};

export const editorStartEvent = (
  prevState: BuilderState,
  nextState: BuilderState,
): CampaignBuilderAnalyticsEvent | null => {
  const channel = nextState.context.activeChannel;
  const stayedInEditor = prevState.step === 'builder' && prevState.context.activeChannel === channel;
  if (nextState.step !== 'builder' || stayedInEditor) {
    return null;
  }

  return channelEvent(nextState, channel, 'editor_start');
};
